/**
 * instantTap.ts — the one budget every instant tap runs under.
 *
 * Card 1219177901097382: a like, a save, a follow or a comment-like paints the
 * moment it is tapped, then settles on what the server answers, and is put back
 * with one line of explanation when the server refuses. The modules that own
 * those taps (listingLikes.ts, savedListings.ts, followUser.ts, commentLikes.ts)
 * each keep their own state, paint and words; what they share is this: how long
 * a tap waits before the screen stops believing it.
 *
 * THREE THINGS COME BACK FROM A WRITE, and the caller treats them differently:
 *   · an answer, 2xx or not: the server's state, to settle on (a refusal is put
 *     back and said in the server's words; a 429 is just one more refusal,
 *     decision 1 of 2026-10-20: no code here reads the limiter's number);
 *   · a thrown fetch (offline, DNS, a refused socket): for the directional
 *     taps nothing needs reading (a resend is idempotent), so the tap is put
 *     back and said; the two toggles read, see below;
 *   · nothing at all within the budget: the server MAY have taken it. The tap
 *     is put back and the server's state is re-read, because for the two taps
 *     that are toggles (save, comment like) a resend would undo what the first
 *     one did. A late answer still settles the press when no newer tap came.
 *
 * 🪤 A RACE, NOT AN AbortSignal. The hazard the budget exists for is a request
 * the transport never settles (api-server routes/listAndEarn.ts documents the
 * Redis offline queue doing exactly that: "THE CLIENT MUST SET ITS OWN TIMEOUT").
 * AbortController only ends a wait the transport agrees to end; a race ends it
 * unconditionally. Same reasoning as promoOpenGate.ts's readPromoOpen.
 *
 * THE ORDER OF EVENTS ON ONE CONTROL (adversarial passes 1 and 2, 2026-10-20).
 * A press is not the only writer of a control's state: the install-time seed
 * (likes, saves) or the profile route's status check (follow) can land while a
 * press is out. Two rules, both pure functions below so every module applies
 * the same ones:
 *   · a read is THE WAY BACK for a press (what a refusal puts back to) only if
 *     it landed after the press painted AND was sent after the control's last
 *     settle. A read sent before a settled write is older than that write, and
 *     a read that landed before the paint is already in the state the paint
 *     read.
 *   · a read must not take a painted press off while its answer is out, nor
 *     wipe a settle newer than the read: the module re-asserts both over it.
 *   · the way back for a press is, in order: a settle newer than the press's
 *     own paint (its re-read landed and settled while a late answer was still
 *     out), else such a read, else the state the screen had at the tap.
 *   · a read re-asserts settles newer than its send FIRST and presses in flight
 *     LAST, because a press's own intent outranks any truth while it is out.
 * Every module keeps its own clock (a counter, not wall time: the order is all
 * that matters, and fake timers must not move it).
 *
 * THE TWO TOGGLES (save, comment like) have no direction in the request, so
 * once the server MAY have processed one, the only honest move is to read: a
 * dropped request can be a connection reset after the write, a 502/504 is a
 * proxy that lost the answer, and an unreadable 2xx is a write that landed.
 * Those put back and re-read like a timeout (the recon's failure table, lane
 * A's scope line). The directional taps (like, follow) put back and say so: a
 * resend of theirs is idempotent on the server.
 *
 * THE NUMBER. 8 seconds: a tap on venue wifi can take a few seconds to answer
 * and must not be undone while it is merely slow, and a heart that stays filled
 * for much longer over a write that never landed is a claim the page cannot
 * back. Every module uses this one constant; change it here.
 */

export const TAP_TIMEOUT_MS = 8000;

export type TapOutcome =
  | { kind: "answer"; res: Response }
  | { kind: "thrown" }
  | { kind: "expired" };

/**
 * The request raced against the budget. Never throws: a rejected request is
 * reported as `thrown`, the budget running out as `expired`. The timer is
 * cleared either way, so a settled tap leaves nothing running.
 */
export async function awaitTap(
  request: Promise<Response>,
  timeoutMs: number = TAP_TIMEOUT_MS,
): Promise<TapOutcome> {
  const attempt = request.then(
    (res): TapOutcome => ({ kind: "answer", res }),
    (): TapOutcome => ({ kind: "thrown" }),
  );
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<TapOutcome>((resolve) => {
    timer = setTimeout(() => resolve({ kind: "expired" }), timeoutMs);
  });
  try {
    return await Promise.race([attempt, expiry]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** A proxy answered for a server that did not: the write may or may not have been processed. */
export function isNoAnswer(status: number): boolean {
  return status === 502 || status === 504;
}

/** A monotonic clock for one module: the order of paints, settles, sends and landings. */
export function createClock(): () => number {
  let t = 0;
  return () => ++t;
}

/** One read of the server's state for a control: what it said, when it was sent, when it landed. */
export interface Read<T> {
  value: T;
  sentAt: number;
  landedAt: number;
}

/**
 * Whether `read` is the way back for a press that painted at `paintedAt` on a
 * control last settled at `settledAt` (undefined: never). See the header.
 */
export function readIsWayBack(read: Read<unknown> | undefined, paintedAt: number, settledAt: number | undefined): boolean {
  if (!read) return false;
  return read.landedAt > paintedAt && read.sentAt > (settledAt ?? -1);
}

/** Whether a settle at `settledAt` is newer than a read sent at `sentAt`, so the read must not wipe it. */
export function settleOutranksRead(settledAt: number | undefined, sentAt: number): boolean {
  return settledAt !== undefined && settledAt > sentAt;
}
